import { XCircle } from "lucide-react";
import { useCallback } from "react";

import { OperationEta } from "@/modules/dashboard/crypto-swaps/components/shared/OperationEta";
import { ColocaCountUp } from "@/shared/components/motion/ColocaCountUp";
import { ColocaSuccessMark } from "@/shared/components/motion/ColocaSuccessMark";
import useTranslations from "@/shared/lang/useTranslations";

import type { CardCopTopUp } from "../../types/cardCopTopUp.types";
import type { CardCopTopUpTiming } from "../../types/cardFundingOptions.types";
import { copLoadTimeline, copLoadWait, type LoadTimelineStageId } from "../../utils/cardFunding";
import { CardLoadTimeline } from "../card-load/CardLoadTimeline";
import { useCopTopUpFormat } from "./copTopUpFormat";
import { CopTopUpRateRow } from "./CopTopUpRateRow";
import { CopTopUpTransit } from "./CopTopUpTransit";

type Face = "processing" | "delivered" | "credited" | "failed";

const STAGE_LABEL: Record<
  LoadTimelineStageId,
  | "card_load_stage_debited"
  | "card_load_stage_sending"
  | "card_load_stage_arrived"
  | "card_load_stage_credited"
> = {
  debited: "card_load_stage_debited",
  sending: "card_load_stage_sending",
  arrived: "card_load_stage_arrived",
  credited: "card_load_stage_credited",
};

function faceOf(status: string): Face {
  if (status === "CREDITED") return "credited";
  if (status === "FAILED") return "failed";
  if (status === "DELIVERED") return "delivered";
  return "processing";
}

export interface CopTopUpResultProps {
  topUp: CardCopTopUp;
  last4: string;
  /** The measured stages of a load; without it the screen shows elapsed time only. */
  timing?: CardCopTopUpTiming | null;
}

/** Where the top-up is: never claims the card has the money before the card says so. */
export function CopTopUpResult({ topUp, last4, timing = null }: CopTopUpResultProps) {
  const { t, activeLanguage } = useTranslations("cards");
  const format = useCopTopUpFormat();
  const face = faceOf(topUp.status);
  const wait = copLoadWait(topUp, timing);
  const stages = copLoadTimeline(topUp);
  const credited = Number(topUp.credited_amount ?? topUp.expected_credit ?? 0);
  const formatUsd = useCallback((value: number) => format(String(value), "USD"), [format]);
  const timeOf = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(activeLanguage, { hour: "2-digit", minute: "2-digit" }).format(new Date(iso))
      : null;

  const body =
    face === "credited"
      ? t("cop_top_up_status_credited_body", {
          amount: format(topUp.credited_amount ?? topUp.expected_credit, "USD"),
          last4,
        })
      : t(`cop_top_up_status_${face}_body`);

  return (
    <div className="flex flex-col gap-5" aria-live="polite">
      <div className="flex flex-col items-center gap-3 pt-2 text-center">
        {face === "credited" ? (
          <ColocaSuccessMark />
        ) : face === "failed" ? (
          <FailedIcon />
        ) : (
          <CopTopUpTransit network={topUp.network} last4={last4} className="max-w-md" />
        )}
        <div className="flex flex-col gap-1.5">
          <h3 className="text-base font-semibold text-foreground">{t(`cop_top_up_status_${face}_title`)}</h3>
          <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
        </div>
      </div>

      {wait && (
        <OperationEta
          since={wait.since}
          estimate={
            wait.estimatedSeconds !== null
              ? {
                  estimatedSeconds: wait.estimatedSeconds,
                  sampleSize: timing?.sample_size ?? 0,
                  trend: timing?.trend ?? null,
                }
              : null
          }
        />
      )}

      <div className="rounded-xl border border-border bg-card p-4">
        <CardLoadTimeline
          aria-label={t("card_load_timeline_aria")}
          steps={stages.map((stage) => ({
            id: stage.id,
            state: stage.state,
            label: stage.state === "failed" ? t("card_load_stage_failed") : t(STAGE_LABEL[stage.id]),
            detail: stage.state === "done" ? timeOf(stage.at) : null,
          }))}
        />
      </div>

      <dl className="grid w-full grid-cols-2 gap-3 overflow-hidden rounded-xl border border-border bg-card p-4 text-left">
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted-foreground">{t("cop_top_up_row_pay")}</dt>
          <dd className="font-mono text-sm font-medium tabular-nums">{format(topUp.send_amount, "COP")}</dd>
        </div>
        {face === "failed" ? (
          <div className="flex flex-col gap-0.5">
            <dt className="text-xs text-muted-foreground">{t("cop_top_up_row_returned")}</dt>
            <dd className="font-mono text-sm font-medium tabular-nums">{format(topUp.send_amount, "COP")}</dd>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            <dt className="text-xs text-muted-foreground">{t("cop_top_up_row_credit")}</dt>
            <dd className="font-mono text-sm font-medium tabular-nums">
              {face === "credited" && topUp.credited_amount ? (
                <ColocaCountUp value={credited} format={formatUsd} />
              ) : topUp.credited_amount ? (
                format(topUp.credited_amount, "USD")
              ) : (
                `≈ ${format(topUp.expected_credit, "USD")}`
              )}
            </dd>
          </div>
        )}
        {face !== "failed" && (
          <CopTopUpRateRow
            rates={topUp}
            locale={activeLanguage}
            className="col-span-2 -mx-4 -mb-4 border-t border-border px-4"
          />
        )}
      </dl>
      {(face === "processing" || face === "delivered") && (
        <p className="text-center text-xs text-muted-foreground">{t("cop_top_up_keep_going")}</p>
      )}
    </div>
  );
}

function FailedIcon() {
  return (
    <span className="flex size-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
      <XCircle aria-hidden="true" className="size-7" />
    </span>
  );
}
